/** @format */

import { apiClient } from "@/api/axiosClient.ts";
import type { PaginatedResponse, PaginationParams } from "./common.ts";
import { handleApiError } from "./common.ts";
import type { VisitsListResponse, VisitStatus } from "./visits.ts";

// Patient domain types aligned with /clinic/patients/ endpoints
export type Gender = "M" | "F" | "O";

export interface PatientListItem {
  id: string; // UUID
  first_name: string;
  last_name: string;
  full_name: string;
  birth_date: string; // YYYY-MM-DD
  age: string;
  gender: Gender;
  phone?: string;
  email?: string;
  address?: string;
}

export interface PatientDetail extends PatientListItem {
  medication_reaction?: string;
  medical_history?: string;
  diseases?: string[];
  other_diseases?: string;
}

export interface PatientLite {
  id: string; // UUID
  label: string;
}

export type PatientsListResponse = PaginatedResponse<PatientListItem>;
export type PatientsLiteResponse = PatientLite[];

export interface CreatePatientRequest {
  first_name: string;
  last_name: string;
  birth_date: string; // YYYY-MM-DD
  gender?: Gender;
  phone?: string;
  email?: string;
  address?: string;
  medication_reaction?: string;
  medical_history?: string;
  diseases?: string[];
  other_diseases?: string;
}

export interface UpdatePatientRequest extends CreatePatientRequest {}

export interface PatientsQueryParams extends PaginationParams {
  search?: string;
  name?: string;
  phone?: string;
  email?: string;
  mrn?: string;
  active?: boolean;
  created_at__date?: string;
  department?: "ophthalmology" | "orl";
}

// API functions for Patients
export const patientsApi = {
  /**
   * Get paginated list of patients
   * GET /clinic/patients/
   */
  async getPatients(
    params?: PatientsQueryParams,
  ): Promise<PatientsListResponse> {
    try {
      const response = await apiClient.get<PatientsListResponse>(
        "/clinic/patients/",
        { params },
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to fetch patients");
    }
  },

  /**
   * Get a simplified list of patients for dropdowns
   * GET /clinic/patients/lite/
   */
  async getPatientsLite(): Promise<PatientsLiteResponse> {
    try {
      const response = await apiClient.get<PatientsLiteResponse>(
        "/clinic/patients/lite/",
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to fetch patients");
    }
  },

  /**
   * Get a single patient by ID
   * GET /clinic/patients/{id}/
   */
  async getPatient(id: string): Promise<PatientDetail> {
    try {
      const response = await apiClient.get<PatientDetail>(
        `/clinic/patients/${id}/`,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to fetch patient");
    }
  },

  /**
   * Create a new patient
   * POST /clinic/patients/
   */
  async createPatient(data: CreatePatientRequest): Promise<PatientDetail> {
    try {
      const response = await apiClient.post<PatientDetail>(
        "/clinic/patients/",
        data,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to create patient");
    }
  },

  /**
   * Update a patient (PUT)
   * PUT /clinic/patients/{id}/
   */
  async updatePatient(
    id: string,
    data: UpdatePatientRequest,
  ): Promise<PatientDetail> {
    try {
      const response = await apiClient.put<PatientDetail>(
        `/clinic/patients/${id}/`,
        data,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to update patient");
    }
  },

  /**
   * Partially update a patient (PATCH)
   * PATCH /clinic/patients/{id}/
   */
  async partialUpdatePatient(
    id: string,
    data: Partial<UpdatePatientRequest>,
  ): Promise<PatientDetail> {
    try {
      const response = await apiClient.patch<PatientDetail>(
        `/clinic/patients/${id}/`,
        data,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to update patient");
    }
  },

  /**
   * Delete a patient
   * DELETE /clinic/patients/{id}/
   */
  async deletePatient(id: string): Promise<void> {
    try {
      await apiClient.delete(`/clinic/patients/${id}/`);
    } catch (error) {
      handleApiError(error, "Failed to delete patient");
    }
  },

  /**
   * Convenience: get visits for a specific patient
   * GET /clinic/patients/{id}/visits/
   */
  async getPatientVisits(
    id: string,
    params?: PaginationParams & { status?: VisitStatus },
  ): Promise<VisitsListResponse> {
    try {
      const response = await apiClient.get<VisitsListResponse>(
        `/clinic/patients/${id}/visits/`,
        { params },
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "Failed to fetch patient visits");
    }
  },
};

// Utilities
export const patientUtils = {
  getFullName(
    patient: Pick<PatientListItem | PatientDetail, "first_name" | "last_name">,
  ): string {
    return `${patient.first_name} ${patient.last_name}`.trim();
  },
};

// Export axios instance for direct use if needed
export { apiClient };
